// Small strip shown at the top of a badged admin page:
//   "3 new since you last looked · Mark all read"
//
// "Read" is only about the red bubble. It never resolves, approves, pays or
// completes the underlying item — the wording here says so explicitly.

import { Button } from "@/components/ui/button";
import { CheckCheck } from "lucide-react";
import { useAdminUnseen } from "@/hooks/useAdminUnseen";
import type { UnseenBadgeKey } from "@/lib/adminUnseen";

interface Props {
  badge: UnseenBadgeKey;
  /** What "read" means here, e.g. "This does not change enrollment." */
  note?: string;
}

export function UnseenReviewBar({ badge, note }: Props) {
  const { counts, markAllSeen } = useAdminUnseen();
  const count = counts[badge] ?? 0;
  if (count <= 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
      <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center leading-none">
        {count > 99 ? "99+" : count}
      </span>
      <span className="text-sm">
        {count === 1 ? "1 item you haven't reviewed yet" : `${count} items you haven't reviewed yet`}
      </span>
      {note && <span className="text-xs text-muted-foreground">{note}</span>}
      <Button
        variant="outline"
        size="sm"
        className="ml-auto gap-1.5"
        onClick={() => void markAllSeen(badge)}
      >
        <CheckCheck className="h-4 w-4" />
        Mark all read
      </Button>
    </div>
  );
}

/** Small "New" chip for a single unseen row. */
export function UnseenDot({ itemKey }: { itemKey: string }) {
  const { isUnseen } = useAdminUnseen();
  if (!isUnseen(itemKey)) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-destructive/10 text-destructive text-[10px] font-semibold px-1.5 py-0.5 leading-none">
      New
    </span>
  );
}
